// src/components/shared/FeedNovidades.tsx
// CREATED: 2026-03-27
// Feed de novidades compartilhado entre portal do aluno e portal do filiado
// Consome GET /novidades (público, sem JWT)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Megaphone, Wrench, Info, Sparkles, Tag,
  Phone, Globe, Mail, ExternalLink, RefreshCw,
  Loader2, AlertTriangle, ChevronDown, X,
  Bell,
} from 'lucide-react';

import { API_BASE } from '../../utils/apiConfig';

// ─── Types ────────────────────────────────────────────────────────────────────
type TipoNovidade = 'servico' | 'informacao' | 'novidade' | 'atualizacao' | 'promocao';

interface Novidade {
  id:           string;
  tipo:         TipoNovidade;
  titulo:       string;
  descricao:    string;
  imageUrl:     string | null;
  botaoLabel:   string | null;
  botaoContato: string | null;
  botaoSite:    string | null;
  botaoEmail:   string | null;
  createdAt:    string;
}

// ─── Config por tipo ──────────────────────────────────────────────────────────
const TIPO_CFG: Record<TipoNovidade, {
  label: string; icon: React.FC<any>;
  bg: string; text: string; border: string;
  barColor: string; dotColor: string;
}> = {
  servico:     { label: 'Serviço',     icon: Wrench,    bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    barColor: '#3b82f6', dotColor: '#3b82f6' },
  informacao:  { label: 'Informação',  icon: Info,      bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200',     barColor: '#0ea5e9', dotColor: '#0ea5e9' },
  novidade:    { label: 'Novidade',    icon: Megaphone, bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   barColor: '#f59e0b', dotColor: '#f59e0b' },
  atualizacao: { label: 'Atualização', icon: Sparkles,  bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  barColor: '#a855f7', dotColor: '#a855f7' },
  promocao:    { label: 'Promoção',    icon: Tag,       bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', barColor: '#22c55e', dotColor: '#22c55e' },
};

const fmt = (d: string) =>
  new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

// ─── Modal "Sobre" ────────────────────────────────────────────────────────────
const ModalSobre: React.FC<{
  n: Novidade;
  onClose: () => void;
}> = ({ n, onClose }) => {
  const cfg = TIPO_CFG[n.tipo] ?? TIPO_CFG.novidade;

  const contatos = [
    n.botaoContato && {
      icon: Phone,
      label: 'WhatsApp / Telefone',
      value: n.botaoContato,
      href: `https://wa.me/${n.botaoContato.replace(/\D/g, '')}`,
      color: '#25d366',
    },
    n.botaoSite && {
      icon: Globe,
      label: 'Site / Link',
      value: n.botaoSite,
      href: n.botaoSite,
      color: '#3b82f6',
    },
    n.botaoEmail && {
      icon: Mail,
      label: 'E-mail',
      value: n.botaoEmail,
      href: `mailto:${n.botaoEmail}`,
      color: '#f59e0b',
    },
  ].filter(Boolean) as { icon: React.FC<any>; label: string; value: string; href: string; color: string }[];

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose}/>
      <div className="absolute inset-0 flex items-end sm:items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* barra colorida */}
          <div className="h-1.5 w-full" style={{ background: cfg.barColor }}/>

          {/* Imagem se existir */}
          {n.imageUrl && (
            <div className="w-full h-48 overflow-hidden">
              <img src={n.imageUrl} alt={n.titulo} className="w-full h-full object-cover"/>
            </div>
          )}

          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full border mb-2 ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                  <cfg.icon className="w-3 h-3"/> {cfg.label}
                </span>
                <h3 className="text-lg font-black text-gray-900 leading-tight">{n.titulo}</h3>
              </div>
              <button onClick={onClose}
                className="w-9 h-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition flex items-center justify-center flex-shrink-0">
                <X className="w-4 h-4 text-gray-500"/>
              </button>
            </div>

            {/* Descrição */}
            <p className="text-sm text-gray-600 leading-relaxed mb-5">{n.descricao}</p>

            {/* Contatos */}
            {contatos.length > 0 && (
              <div className="space-y-2.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Como nos contatar</p>
                {contatos.map(({ icon: Icon, label, value, href, color }) => (
                  <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3.5 rounded-2xl border border-gray-100 bg-gray-50
                      hover:border-gray-200 hover:bg-white transition-all group">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: color + '18' }}>
                      <Icon className="w-4 h-4" style={{ color }}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-gray-400 font-semibold">{label}</p>
                      <p className="text-sm font-bold text-gray-800 truncate">{value}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors"/>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 pb-6">
            <button onClick={onClose}
              className="w-full py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Card de novidade ─────────────────────────────────────────────────────────
const CardNovidade: React.FC<{
  n: Novidade;
  onSobre: (n: Novidade) => void;
}> = ({ n, onSobre }) => {
  const [expandida, setExpandida] = useState(false);
  const cfg = TIPO_CFG[n.tipo] ?? TIPO_CFG.novidade;
  const Icon = cfg.icon;
  const temBotao = n.botaoLabel || n.botaoContato || n.botaoSite || n.botaoEmail;
  const textoLongo = n.descricao.length > 180;

  return (
    <div className="bg-white rounded-2xl border border-gray-100
      shadow-[0_4px_20px_-8px_rgba(0,0,0,.12)]
      hover:shadow-[0_8px_30px_-8px_rgba(0,0,0,.18)]
      transition-all overflow-hidden">

      {/* Barra colorida */}
      <div className="h-1 w-full" style={{ background: cfg.barColor }}/>

      {/* Imagem */}
      {n.imageUrl && (
        <div className="w-full h-40 overflow-hidden">
          <img src={n.imageUrl} alt={n.titulo}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
        </div>
      )}

      <div className="p-5">
        {/* Tipo + data */}
        <div className="flex items-center justify-between mb-3">
          <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
            <Icon className="w-3 h-3"/> {cfg.label}
          </span>
          <span className="text-[10px] text-gray-400 font-medium">{fmt(n.createdAt)}</span>
        </div>

        {/* Título */}
        <h3 className="font-black text-gray-900 text-sm leading-snug mb-2">{n.titulo}</h3>

        {/* Descrição com expandir */}
        <p className={`text-xs text-gray-500 leading-relaxed ${!expandida && textoLongo ? 'line-clamp-3' : ''}`}>
          {n.descricao}
        </p>
        {textoLongo && (
          <button onClick={() => setExpandida(v => !v)}
            className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors">
            {expandida ? 'Ver menos' : 'Ver mais'}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandida ? 'rotate-180' : ''}`}/>
          </button>
        )}

        {/* Botão "Sobre" */}
        {temBotao && (
          <button onClick={() => onSobre(n)}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
              text-xs font-bold transition-all hover:shadow-md"
            style={{ background: cfg.barColor + '15', color: cfg.barColor, border: `1.5px solid ${cfg.barColor}30` }}>
            <ExternalLink className="w-3.5 h-3.5"/>
            {n.botaoLabel || 'Saiba mais'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Feed Principal ───────────────────────────────────────────────────────────
interface FeedNovidadesProps {
  maxItems?: number;       // quantos mostrar inicialmente (default 6)
  compact?:  boolean;      // layout compacto para widgets menores
}

const FeedNovidades: React.FC<FeedNovidadesProps> = ({ maxItems = 6, compact = false }) => {
  const [novidades,    setNovidades]    = useState<Novidade[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [erro,         setErro]         = useState<string | null>(null);
  const [selecionada,  setSelecionada]  = useState<Novidade | null>(null);
  const [mostrarTodas, setMostrarTodas] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const res = await fetch(`${API_BASE}/novidades?limit=30`);
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setNovidades(data.novidades ?? []);
    } catch (e: any) {
      setErro(e.message ?? 'Erro ao carregar');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const visiveis = mostrarTodas ? novidades : novidades.slice(0, maxItems);
  const temMais  = novidades.length > maxItems;

  if (loading) return (
    <div className="flex items-center justify-center py-10">
      <Loader2 className="w-6 h-6 text-amber-500 animate-spin"/>
    </div>
  );

  if (erro) return (
    <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0"/>
      <p className="text-sm text-red-600">{erro}</p>
      <button onClick={carregar} className="ml-auto p-1.5 hover:bg-red-100 rounded-lg transition-colors">
        <RefreshCw className="w-3.5 h-3.5 text-red-400"/>
      </button>
    </div>
  );

  if (novidades.length === 0) return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
        style={{ background: 'rgba(212,175,55,0.12)' }}>
        <Bell className="w-6 h-6 text-amber-500"/>
      </div>
      <p className="text-sm font-medium text-gray-500">Nenhuma novidade no momento</p>
      <p className="text-xs text-gray-400 mt-1">Volte em breve para novidades da Federação</p>
    </div>
  );

  return (
    <>
      {/* Header */}
      {!compact && (
  <div className="flex items-center justify-between mb-5">
    <div className="flex items-center gap-3">
      {/* ✅ Imagem direta, sem fundo, sem filter */}
      <img src="/icons/icone16.png" alt="novidades" className="w-8 h-8 object-contain" />
      <div>
        <p className="text-sm font-black text-gray-900">Novidades da Federação</p>
        <p className="text-[10px] text-gray-400">{novidades.length} publicação{novidades.length !== 1 ? 'ões' : ''}</p>
      </div>
    </div>
    <button onClick={carregar}
      className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
      <RefreshCw className="w-3.5 h-3.5"/>
    </button>
  </div>
)}
   

      {/* Grid */}
      <div className={`grid gap-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
        {visiveis.map(n => (
          <CardNovidade key={n.id} n={n} onSobre={setSelecionada}/>
        ))}
      </div>

      {/* Ver mais */}
      {temMais && !mostrarTodas && (
        <button onClick={() => setMostrarTodas(true)}
          className="mt-5 w-full py-3 rounded-2xl border-2 border-dashed border-gray-200
            text-sm font-semibold text-gray-400 hover:border-amber-300 hover:text-amber-600
            transition-all flex items-center justify-center gap-2">
          <ChevronDown className="w-4 h-4"/>
          Ver mais {novidades.length - maxItems} publicação{novidades.length - maxItems !== 1 ? 'ões' : ''}
        </button>
      )}

      {/* Modal "Sobre" */}
      {selecionada && (
        <ModalSobre n={selecionada} onClose={() => setSelecionada(null)}/>
      )}
    </>
  );
};

export default FeedNovidades;